import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useState, useEffect, useRef } from 'react';
import { 
  Key, 
  Plus, 
  RefreshCw, 
  Trash2, 
  Copy, 
  Webhook,
  Save,
  TestTube,
  CheckCircle,
  XCircle,
  MessageCircle,
  Code,
  AlertCircle,
  Clock,
  Calendar,
  Edit,
  DollarSign,
  Receipt,
  Umbrella,
  Bot,
  Upload,
  FileText,
  Loader2,
  Users,
  Play,
  BarChart2
} from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { useDataCache } from '../../hooks/useDataCache';
import EmailConfigurationSettings from '@/components/EmailConfigurationSettings';
import { AvailabilityScheduler, type TiemposDisponibles } from '@/components/AvailabilityScheduler';
import { HolidayTimeRanges, type HorarioRango } from '@/components/HolidayTimeRanges';
import {
  getInstitutionSchedules,
  createInstitutionSchedule,
  updateInstitutionSchedule,
  deleteInstitutionSchedule,
  type InstitutionSchedule,
  type InstitutionScheduleInput,
} from '@/hooks/institutionSchedulesService';
import {
  getHolidays,
  createHoliday,
  updateHoliday,
  deleteHoliday,
  type Holiday,
  type HolidayInput,
} from '@/hooks/holidaysService';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import BillingSettings from '@/pages/cfdi/BillingSettings';

interface ApiKey {
  id: number;
  name: string;
  isActive: boolean;
  createdAt: string;
  lastUsedAt?: string;
  key?: string; // Solo disponible al crear/regenerar
}

interface CrmWebhookConfig {
  enabled: boolean;
  url: string;
  secretKey: string;
  events: string[];
  lastTestResult?: {
    success: boolean;
    timestamp: string;
    error?: string;
  };
}

export default function Admin() {
  const { toast } = useToast();
  const { getApiKeysData, createApiKey, getCompanyData, cachedData, invalidateCache } = useDataCache();

  // API Keys State
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [showCreateApiKeyForm, setShowCreateApiKeyForm] = useState(false);
  const [newApiKeyName, setNewApiKeyName] = useState('');
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);

  // CRM Webhook State
  const [webhookConfig, setWebhookConfig] = useState<CrmWebhookConfig>({
    enabled: false,
    url: '',
    secretKey: '',
    events: ['student_created', 'student_updated', 'api_created', 'api_updated']
  });
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);

  // AI Bubble State
  const [customScript, setCustomScript] = useState('');
  const [scriptLoading, setScriptLoading] = useState(false);
  const [scriptSuccess, setScriptSuccess] = useState(false);
  const [scriptError, setScriptError] = useState<string | null>(null);

  // Institution Schedules State
  const [schedules, setSchedules] = useState<InstitutionSchedule[]>([]);
  const [schedulesLoading, setSchedulesLoading] = useState(true);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<InstitutionSchedule | null>(null);
  const [scheduleFormData, setScheduleFormData] = useState<{
    name: string;
    description: string;
    tiemposDisponibles: TiemposDisponibles | null;
    isActive: boolean;
  }>({
    name: '',
    description: '',
    tiemposDisponibles: null,
    isActive: true,
  });

  // Holidays State
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [holidaysLoading, setHolidaysLoading] = useState(true);
  const [isHolidayDialogOpen, setIsHolidayDialogOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [holidayFormData, setHolidayFormData] = useState<{
    fecha: string;
    festividad: string;
    tiemposDisponibles: HorarioRango[] | null;
    isActive: boolean;
  }>({
    fecha: '',
    festividad: '',
    tiemposDisponibles: null,
    isActive: true,
  });

  // Finance Settings State
  const [financeSettings, setFinanceSettings] = useState<{
    defaultCurrency: string;
    salesAccountId: number | null;
    arAccountId: number | null;
    taxPayableAccountId: number | null;
    bankAccountId: number | null;
    discountAccountId: number | null;
    surchargeAccountId: number | null;
    creditAccountId: number | null;
    tuitionEligibleStatuses: string[];
    inscriptionProductId: number | null;
  }>({
    defaultCurrency: 'MXN',
    salesAccountId: null,
    arAccountId: null,
    taxPayableAccountId: null,
    bankAccountId: null,
    discountAccountId: null,
    surchargeAccountId: null,
    creditAccountId: null,
    tuitionEligibleStatuses: ['activo', 'aceptado', 'aspirante'],
    inscriptionProductId: null,
  });
  const [newStatusInput, setNewStatusInput] = useState('');
  const [financeSettingsLoading, setFinanceSettingsLoading] = useState(true);
  const [financeSaving, setFinanceSaving] = useState(false);
  const [accounts, setAccounts] = useState<Array<{ id: number; code: string; name: string; type: string }>>([]);
  const [productsList, setProductsList] = useState<Array<{ id: number; sku: string; name: string; price: string }>>([]);

  // Skip Holidays State
  const [skipHolidays, setSkipHolidays] = useState(true);
  const [skipHolidaysSaving, setSkipHolidaysSaving] = useState(false);

  // Debt Status Filter State (Estatus Alumnos)
  const [debtStatusFilterEnabled, setDebtStatusFilterEnabled] = useState(true);
  const [debtStatusFilterSaving, setDebtStatusFilterSaving] = useState(false);

  // Vacation Periods State
  interface VacationPeriod {
    id: number;
    nombre: string;
    fechaInicio: string;
    fechaFin: string;
    isActive: boolean;
  }
  const [vacationPeriodsList, setVacationPeriodsList] = useState<VacationPeriod[]>([]);
  const [vacationPeriodsLoading, setVacationPeriodsLoading] = useState(true);
  const [isVacationDialogOpen, setIsVacationDialogOpen] = useState(false);
  const [editingVacation, setEditingVacation] = useState<VacationPeriod | null>(null);
  const [vacationFormData, setVacationFormData] = useState({ nombre: '', fechaInicio: '', fechaFin: '', isActive: true });
  const [vacationSaving, setVacationSaving] = useState(false);

  // Local AI Agent State
  interface AgentDocument {
    id: number;
    fileName: string;
    fileType: string;
    fileSize?: number | null;
    isProcessed: boolean;
    summary?: string | null;
    createdAt: string;
  }
  interface AgentConfigData {
    id: number;
    name: string;
    personality?: string | null;
    objective?: string | null;
    systemDescription?: string | null;
    rules?: string | null;
    guidelines?: string | null;
    guardrails?: string | null;
    isActive: boolean;
  }
  const [agentConfig, setAgentConfig] = useState<AgentConfigData>({
    id: 0,
    name: 'Asistente',
    personality: '',
    objective: '',
    systemDescription: '',
    rules: '',
    guidelines: '',
    guardrails: '',
    isActive: false,
  });
  const [agentDocuments, setAgentDocuments] = useState<AgentDocument[]>([]);
  const [agentLoading, setAgentLoading] = useState(true);
  const [agentSaving, setAgentSaving] = useState(false);
  const [agentDocUploading, setAgentDocUploading] = useState(false);
  const agentFileInputRef = useRef<HTMLInputElement>(null);

  // Rules manifest modal
  interface RuleTransition { from: string | string[]; to: string; condition: string; }
  interface RuleEntry {
    id: string;
    description: string;
    eligibleStatuses: string[];
    transitions: RuleTransition[];
    triggers: string[];
  }
  interface RulesManifest { rules: RuleEntry[]; triggerMap: Record<string, string[]>; }
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [rulesManifest, setRulesManifest] = useState<RulesManifest | null>(null);
  const [rulesLoading, setRulesLoading] = useState(false);

  const loadRulesManifest = async () => {
    setShowRulesModal(true);
    if (rulesManifest) return; // already loaded
    setRulesLoading(true);
    try {
      const res = await fetch('/api/alumnos/status-engine/rules', { credentials: 'include' });
      if (res.ok) setRulesManifest(await res.json());
    } finally {
      setRulesLoading(false);
    }
  };

  // Batch status evaluator
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchResult, setBatchResult] = useState<{
    total: number; applied: number; skipped: number; errors: number; durationMs: number;
  } | null>(null);
  const [batchError, setBatchError] = useState<string | null>(null);

  const runBatchEval = async () => {
    setBatchRunning(true);
    setBatchResult(null);
    setBatchError(null);
    try {
      const res = await fetch('/api/alumnos/batch-evaluate-statuses', {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Error ${res.status}`);
      }
      const data = await res.json();
      setBatchResult(data);
      toast({ title: 'Evaluación completada', description: `${data.applied} alumnos con estatus actualizado de ${data.total} evaluados.` });
    } catch (err) {
      setBatchError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setBatchRunning(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadApiKeys();
    loadWebhookConfig();
    loadAIBubbleScript();
    fetchSchedules();
    fetchHolidays();
    fetchVacationPeriods();
    loadFinanceSettings();
    loadAccounts();
    loadProducts();
    loadSkipHolidays();
    loadAgentConfig();
    loadDebtStatusFilter();
  }, []);

  const loadApiKeys = async () => {
    try {
      setLoading(true);
      // Use cached data from useDataCache
      const apiKeysData = await getApiKeysData();
      setApiKeys(apiKeysData);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Error al cargar API keys",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSkipHolidays = async () => {
    try {
      const response = await fetch('/api/schedule-rules', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSkipHolidays(data.skipHolidays ?? true);
      }
    } catch (error) {
      // silent - no blocker
    }
  };

  const saveSkipHolidays = async (value: boolean) => {
    setSkipHolidaysSaving(true);
    try {
      const rulesRes = await fetch('/api/schedule-rules', { credentials: 'include' });
      const current = rulesRes.ok ? await rulesRes.json() : { rules: '' };
      const response = await fetch('/api/schedule-rules', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rules: current.rules || ' ', skipHolidays: value }),
      });
      if (response.ok) {
        setSkipHolidays(value);
        toast({ title: value ? 'Se saltarán los días festivos en la planificación' : 'Los días festivos no se saltarán en la planificación' });
      }
    } catch (error) {
      toast({ title: 'Error al guardar configuración', variant: 'destructive' });
    } finally {
      setSkipHolidaysSaving(false);
    }
  };

  const loadDebtStatusFilter = async () => {
    try {
      const response = await fetch('/api/alumnos/status-settings', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setDebtStatusFilterEnabled(data.debtStatusFilterEnabled ?? true);
      }
    } catch (error) {
      // silent - no blocker
    }
  };

  const saveDebtStatusFilter = async (value: boolean) => {
    setDebtStatusFilterSaving(true);
    try {
      const response = await fetch('/api/alumnos/status-settings', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ debtStatusFilterEnabled: value }),
      });
      if (response.ok) {
        setDebtStatusFilterEnabled(value);
        toast({
          title: value
            ? 'Filtro de estatus por adeudo activado: los alumnos con adeudo o aspirantes no podrán registrar asistencia ni calificaciones'
            : 'Filtro de estatus por adeudo desactivado: se mostrarán también los alumnos deudores, aspirantes y en baja administrativa',
        });
      }
    } catch (error) {
      toast({ title: 'Error al guardar configuración', variant: 'destructive' });
    } finally {
      setDebtStatusFilterSaving(false);
    }
  };

  const loadWebhookConfig = async () => {
    try {
      // Cargar configuración del webhook desde variables de entorno o base de datos
      const response = await fetch('/api/admin/webhook-config', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setWebhookConfig(data);
      }
    } catch (error) {
      console.log('Webhook config not found, using defaults');
    }
  };

  const loadAIBubbleScript = async () => {
    try {
      const companyData = await getCompanyData();
      setCustomScript(companyData?.customScript || '');
    } catch (error) {
      console.log('AI Bubble script not found, using default');
    }
  };

  const loadFinanceSettings = async () => {
    try {
      setFinanceSettingsLoading(true);
      const response = await fetch('/api/finance/settings', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setFinanceSettings({
          defaultCurrency: data.defaultCurrency || 'MXN',
          salesAccountId: data.salesAccountId,
          arAccountId: data.arAccountId,
          taxPayableAccountId: data.taxPayableAccountId,
          bankAccountId: data.bankAccountId,
          discountAccountId: data.discountAccountId ?? null,
          surchargeAccountId: data.surchargeAccountId ?? null,
          creditAccountId: data.creditAccountId ?? null,
          tuitionEligibleStatuses: Array.isArray(data.tuitionEligibleStatuses) && data.tuitionEligibleStatuses.length > 0
            ? data.tuitionEligibleStatuses
            : ['activo', 'aceptado', 'aspirante'],
          inscriptionProductId: data.inscriptionProductId ?? null,
        });
      }
    } catch (error) {
      console.log('Finance settings not found, using defaults');
    } finally {
      setFinanceSettingsLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await fetch('/api/sales/products', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProductsList(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.log('Error loading products');
    }
  };

  const loadAccounts = async () => {
    try {
      const response = await fetch('/api/finance/accounts', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.map((acc: any) => ({
          id: acc.id,
          code: acc.code,
          name: acc.name,
          type: acc.type,
        })));
      }
    } catch (error) {
      console.log('Error loading accounts');
    }
  };

  const saveFinanceSettings = async () => {
    try {
      setFinanceSaving(true);
      const response = await fetch('/api/finance/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(financeSettings),
      });
      
      if (response.ok) {
        toast({
          title: "Configuración guardada",
          description: "La configuración financiera se ha actualizado correctamente",
        });
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Error al guardar');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Error al guardar configuración",
        variant: "destructive"
      });
    } finally {
      setFinanceSaving(false);
    }
  };

  const handleCreateApiKey = async () => {
    if (!newApiKeyName.trim()) {
      toast({
        title: "Error",
        description: "El nombre es requerido",
        variant: "destructive"
      });
      return;
    }

    try {
      setApiKeyLoading(true);
      const data = await createApiKey(newApiKeyName.trim());
      setCreatedApiKey(data.apiKey.key);
      setNewApiKeyName('');
      setShowCreateApiKeyForm(false);
      loadApiKeys();
      
      toast({
        title: "¡Éxito!",
        description: "API Key creada exitosamente. ¡Guárdala ahora!",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Error al crear API key",
        variant: "destructive"
      });
    } finally {
      setApiKeyLoading(false);
    }
  };

  const regenerateApiKey = async (id: number, name: string) => {
    if (!confirm(`¿Estás seguro de que quieres regenerar la API Key "${name}"? La clave anterior dejará de funcionar.`)) {
      return;
    }

    try {
      setApiKeyLoading(true);
      const response = await fetch(`/api/apikeys/${id}/regenerate`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error al regenerar API key');
      }

      const data = await response.json();
      setCreatedApiKey(data.apiKey.key);
      loadApiKeys();
      
      toast({
        title: "¡Éxito!",
        description: "API Key regenerada exitosamente. ¡Guarda la nueva clave!",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Error al regenerar API key",
        variant: "destructive"
      });
    } finally {
      setApiKeyLoading(false);
    }
  };

  const deleteApiKey = async (id: number, name: string) => {
    if (!confirm(`¿Estás seguro de que quieres eliminar la API Key "${name}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/apikeys/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error al eliminar API key');
      }

      loadApiKeys();
      toast({
        title: "¡Éxito!",
        description: "API Key eliminada exitosamente",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Error al eliminar API key",
        variant: "destructive"
      });
    }
  };

  const saveWebhookConfig = async () => {
    try {
      setWebhookLoading(true);
      const response = await fetch('/api/admin/webhook-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(webhookConfig)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error al guardar configuración');
      }

      toast({
        title: "¡Éxito!",
        description: "Configuración de webhook guardada exitosamente",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Error al guardar configuración",
        variant: "destructive"
      });
    } finally {
      setWebhookLoading(false);
    }
  };

  const testWebhook = async () => {
    try {
      setTestingWebhook(true);
      const response = await fetch('/api/admin/webhook-test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url: webhookConfig.url, secretKey: webhookConfig.secretKey })
      });

      const result = await response.json();
      
      setWebhookConfig(prev => ({
        ...prev,
        lastTestResult: {
          success: response.ok,
          timestamp: new Date().toISOString(),
          error: response.ok ? undefined : result.error
        }
      }));

      toast({
        title: response.ok ? "¡Éxito!" : "Error",
        description: response.ok ? "Webhook probado exitosamente" : result.error,
        variant: response.ok ? "default" : "destructive"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Error al probar webhook",
        variant: "destructive"
      });
    } finally {
      setTestingWebhook(false);
    }
  };

  const saveAIBubbleScript = async () => {
    try {
      setScriptLoading(true);
      setScriptError(null);
      
      const response = await fetch('/api/company', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          legalName: cachedData.company?.legalName,
          rfc: cachedData.company?.rfc,
          website: cachedData.company?.website,
          customScript: customScript,
        }),
      });
      
      if (response.ok) {
        invalidateCache('company');
        await getCompanyData();
        setScriptSuccess(true);
        setTimeout(() => setScriptSuccess(false), 3000);
        toast({
          title: "¡Éxito!",
          description: "Script de la burbuja guardado exitosamente",
        });
      } else {
        const errorData = await response.json();
        setScriptError(errorData.error || 'Error al guardar el script');
        toast({
          title: "Error",
          description: errorData.error || 'Error al guardar el script',
          variant: "destructive"
        });
      }
    } catch (error) {
      setScriptError('Error al guardar el script de la burbuja de AI');
      toast({
        title: "Error",
        description: "Error al guardar el script de la burbuja de AI",
        variant: "destructive"
      });
    } finally {
      setScriptLoading(false);
    }
  };

  // ── Local AI Agent Functions ────────────────────────────────────────────────
  const loadAgentConfig = async () => {
    try {
      setAgentLoading(true);
      const res = await fetch('/api/agent-config', { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      if (data.config) {
        setAgentConfig({
          id: data.config.id,
          name: data.config.name ?? 'Asistente',
          personality: data.config.personality ?? '',
          objective: data.config.objective ?? '',
          systemDescription: data.config.systemDescription ?? '',
          rules: data.config.rules ?? '',
          guidelines: data.config.guidelines ?? '',
          guardrails: data.config.guardrails ?? '',
          isActive: data.config.isActive ?? false,
        });
      }
      setAgentDocuments(data.documents ?? []);
    } catch (err) {
      console.error('Error loading agent config', err);
    } finally {
      setAgentLoading(false);
    }
  };

  const saveAgentConfig = async () => {
    try {
      setAgentSaving(true);
      const res = await fetch('/api/agent-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: agentConfig.name || 'Asistente',
          personality: agentConfig.personality || null,
          objective: agentConfig.objective || null,
          systemDescription: agentConfig.systemDescription || null,
          rules: agentConfig.rules || null,
          guidelines: agentConfig.guidelines || null,
          guardrails: agentConfig.guardrails || null,
          isActive: agentConfig.isActive,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setAgentConfig(prev => ({ ...prev, id: data.config.id }));
        toast({ title: '¡Éxito!', description: 'Configuración del agente guardada' });
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.error || 'Error al guardar', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Error de conexión', variant: 'destructive' });
    } finally {
      setAgentSaving(false);
    }
  };

  const uploadAgentDocument = async (file: File) => {
    try {
      setAgentDocUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/agent-config/documents', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setAgentDocuments(prev => [...prev, data.document]);
        toast({ title: '¡Éxito!', description: `"${file.name}" procesado y añadido al agente` });
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.error || 'Error al subir documento', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Error al subir documento', variant: 'destructive' });
    } finally {
      setAgentDocUploading(false);
      if (agentFileInputRef.current) agentFileInputRef.current.value = '';
    }
  };

  const deleteAgentDocument = async (docId: number) => {
    if (!confirm('¿Eliminar este documento del agente?')) return;
    try {
      const res = await fetch(`/api/agent-config/documents/${docId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        setAgentDocuments(prev => prev.filter(d => d.id !== docId));
        toast({ title: '¡Éxito!', description: 'Documento eliminado' });
      } else {
        toast({ title: 'Error', description: 'No se pudo eliminar', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Error de conexión', variant: 'destructive' });
    }
  };

  // Institution Schedules Functions
  const fetchSchedules = async () => {
    try {
      const data = await getInstitutionSchedules();
      setSchedules(data);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Error al cargar horarios',
      });
    } finally {
      setSchedulesLoading(false);
    }
  };

  const handleScheduleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const scheduleData: InstitutionScheduleInput = {
        name: scheduleFormData.name,
        description: scheduleFormData.description || null,
        tiemposDisponibles: scheduleFormData.tiemposDisponibles,
        isActive: scheduleFormData.isActive,
      };

      if (editingSchedule) {
        await updateInstitutionSchedule(editingSchedule.id, scheduleData);
        toast({
          title: 'Éxito',
          description: 'Horario actualizado correctamente',
        });
      } else {
        await createInstitutionSchedule(scheduleData);
        toast({
          title: 'Éxito',
          description: 'Horario creado correctamente',
        });
      }

      setIsScheduleDialogOpen(false);
      resetScheduleForm();
      fetchSchedules();
    } catch (error: any) {
      console.error('Error saving schedule:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.error || 'Error al guardar horario',
      });
    }
  };

  const handleEditSchedule = (schedule: InstitutionSchedule) => {
    setEditingSchedule(schedule);
    setScheduleFormData({
      name: schedule.name,
      description: schedule.description || '',
      tiemposDisponibles: schedule.tiemposDisponibles || null,
      isActive: schedule.isActive,
    });
    setIsScheduleDialogOpen(true);
  };

  const handleDeleteSchedule = async (id: number) => {
    if (!confirm('¿Estás seguro de eliminar este horario?')) return;

    try {
      await deleteInstitutionSchedule(id);
      toast({
        title: 'Éxito',
        description: 'Horario eliminado correctamente',
      });
      fetchSchedules();
    } catch (error: any) {
      console.error('Error deleting schedule:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.error || 'Error al eliminar horario',
      });
    }
  };

  const resetScheduleForm = () => {
    setEditingSchedule(null);
    setScheduleFormData({
      name: '',
      description: '',
      tiemposDisponibles: null,
      isActive: true,
    });
  };

  // Holidays Functions
  const fetchHolidays = async () => {
    try {
      const data = await getHolidays();
      setHolidays(data);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Error al cargar días festivos',
      });
    } finally {
      setHolidaysLoading(false);
    }
  };

  const handleHolidaySubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const holidayData: HolidayInput = {
        fecha: holidayFormData.fecha,
        festividad: holidayFormData.festividad,
        tiemposDisponibles: holidayFormData.tiemposDisponibles,
        isActive: holidayFormData.isActive,
      };

      if (editingHoliday) {
        await updateHoliday(editingHoliday.id, holidayData);
        toast({
          title: 'Éxito',
          description: 'Día festivo actualizado correctamente',
        });
      } else {
        await createHoliday(holidayData);
        toast({
          title: 'Éxito',
          description: 'Día festivo creado correctamente',
        });
      }

      setIsHolidayDialogOpen(false);
      resetHolidayForm();
      fetchHolidays();
    } catch (error: any) {
      console.error('Error saving holiday:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.error || 'Error al guardar día festivo',
      });
    }
  };

  const handleEditHoliday = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    const fechaStr = typeof holiday.fecha === 'string' 
      ? holiday.fecha.split('T')[0] 
      : new Date(holiday.fecha).toISOString().split('T')[0];
    
    setHolidayFormData({
      fecha: fechaStr,
      festividad: holiday.festividad,
      tiemposDisponibles: holiday.tiemposDisponibles || null,
      isActive: holiday.isActive,
    });
    setIsHolidayDialogOpen(true);
  };

  const handleDeleteHoliday = async (id: number) => {
    if (!confirm('¿Estás seguro de eliminar este día festivo?')) return;

    try {
      await deleteHoliday(id);
      toast({
        title: 'Éxito',
        description: 'Día festivo eliminado correctamente',
      });
      fetchHolidays();
    } catch (error: any) {
      console.error('Error deleting holiday:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.error || 'Error al eliminar día festivo',
      });
    }
  };

  const resetHolidayForm = () => {
    setEditingHoliday(null);
    setHolidayFormData({
      fecha: '',
      festividad: '',
      tiemposDisponibles: null,
      isActive: true,
    });
  };

  // Vacation Periods Functions
  const fetchVacationPeriods = async () => {
    setVacationPeriodsLoading(true);
    try {
      const res = await fetch('/api/vacation-periods', { credentials: 'include' });
      if (!res.ok) throw new Error('Error al cargar periodos vacacionales');
      const data = await res.json();
      setVacationPeriodsList(data);
    } catch (error) {
      console.error('Error fetching vacation periods:', error);
      toast({ variant: 'destructive', title: 'Error', description: 'Error al cargar periodos vacacionales' });
    } finally {
      setVacationPeriodsLoading(false);
    }
  };

  const handleVacationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setVacationSaving(true);
    try {
      const method = editingVacation ? 'PUT' : 'POST';
      const url = editingVacation ? `/api/vacation-periods/${editingVacation.id}` : '/api/vacation-periods';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(vacationFormData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al guardar');
      toast({ title: 'Éxito', description: editingVacation ? 'Periodo actualizado' : 'Periodo creado' });
      setIsVacationDialogOpen(false);
      setEditingVacation(null);
      setVacationFormData({ nombre: '', fechaInicio: '', fechaFin: '', isActive: true });
      fetchVacationPeriods();
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error.message || 'Error al guardar periodo vacacional' });
    } finally {
      setVacationSaving(false);
    }
  };

  const handleEditVacation = (vp: VacationPeriod) => {
    setEditingVacation(vp);
    setVacationFormData({
      nombre: vp.nombre,
      fechaInicio: vp.fechaInicio.split('T')[0],
      fechaFin: vp.fechaFin.split('T')[0],
      isActive: vp.isActive,
    });
    setIsVacationDialogOpen(true);
  };

  const handleDeleteVacation = async (id: number) => {
    if (!confirm('¿Estás seguro de eliminar este periodo vacacional?')) return;
    try {
      const res = await fetch(`/api/vacation-periods/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Error al eliminar');
      toast({ title: 'Éxito', description: 'Periodo vacacional eliminado' });
      fetchVacationPeriods();
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error.message || 'Error al eliminar' });
    }
  };

  const formatDateRange = (inicio: string, fin: string): string => {
    const fmt = (d: string) => {
      const [y, m, day] = d.split('-');
      return `${day}/${m}/${y}`;
    };
    return inicio === fin ? fmt(inicio) : `${fmt(inicio)} — ${fmt(fin)}`;
  };

  const countVacationDays = (inicio: string, fin: string): number => {
    const s = new Date(inicio + 'T00:00:00Z');
    const e = new Date(fin + 'T00:00:00Z');
    return Math.round((e.getTime() - s.getTime()) / 86400000) + 1;
  };

  const formatTiemposDisponibles = (tiemposDisponibles: HorarioRango[] | null): string => {
    if (!tiemposDisponibles || tiemposDisponibles.length === 0) {
      return 'Día completo';
    }
    
    return tiemposDisponibles
      .map(rango => `${rango.inicio} - ${rango.fin}`)
      .join(', ');
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "¡Copiado!",
        description: "API Key copiada al portapapeles",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo copiar al portapapeles",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p>Cargando configuración...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold mb-2">Configuración General</h1>
        <p className="text-gray-600">
          Gestiona las API Keys, webhooks del CRM y la burbuja de chat inteligente.
        </p>
      </div>

      {/* @ts-ignore - Type conflict between React and Radix UI versions */}
      <Tabs defaultValue="horarios" className="w-full">
        <TabsList className="flex w-full overflow-x-auto h-auto flex-wrap gap-1 justify-start pb-1">
          <TabsTrigger value="horarios" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Horarios
          </TabsTrigger>
          <TabsTrigger value="holidays" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Días Festivos
          </TabsTrigger>
          <TabsTrigger value="vacation-periods" className="flex items-center gap-2">
            <Umbrella className="w-4 h-4" />
            Periodos Vacacionales
          </TabsTrigger>
          <TabsTrigger value="finanzas" className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Finanzas
          </TabsTrigger>
          <TabsTrigger value="facturacion" className="flex items-center gap-2">
            <Receipt className="w-4 h-4" />
            Facturación
          </TabsTrigger>
          <TabsTrigger value="apikeys" className="flex items-center gap-2">
            <Key className="w-4 h-4" />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="webhooks" className="flex items-center gap-2">
            <Webhook className="w-4 h-4" />
            CRM Webhook
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Email
          </TabsTrigger>
          <TabsTrigger value="aibubble" className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4" />
            Burbuja de AI
          </TabsTrigger>
          <TabsTrigger value="estatus-alumnos" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Estatus Alumnos
          </TabsTrigger>
          <TabsTrigger value="agente-local" className="flex items-center gap-2">
            <Bot className="w-4 h-4" />
            Agente Local
          </TabsTrigger>
        </TabsList>

        {/* Horarios Tab */}
        <TabsContent value="horarios">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Horarios de Institución
                  </CardTitle>
                  <CardDescription>
                    Gestiona los horarios disponibles de la institución para la planificación de clases
                  </CardDescription>
                </div>
                <Button
                  onClick={() => {
                    resetScheduleForm();
                    setIsScheduleDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo Horario
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {schedulesLoading ? (
                <p className="text-gray-500 text-center py-8">Cargando...</p>
              ) : schedules.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No hay horarios registrados</p>
                </div>
              ) : (
                <div className="w-full">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Nombre
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Descripción
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Estado
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {schedules.map((schedule) => (
                        <tr key={schedule.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {schedule.name}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {schedule.description || 'Sin descripción'}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <span
                              className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                                schedule.isActive
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {schedule.isActive ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleEditSchedule(schedule)}
                              className="text-blue-600 hover:text-blue-900 mr-4"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSchedule(schedule.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Dialog open={isScheduleDialogOpen} onOpenChange={setIsScheduleDialogOpen}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingSchedule ? 'Editar Horario' : 'Nuevo Horario'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleScheduleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="name">Nombre del Horario *</Label>
                    <Input
                      id="name"
                      value={scheduleFormData.name}
                      onChange={(e) => setScheduleFormData({ ...scheduleFormData, name: e.target.value })}
                      required
                      placeholder="Ej: Horario Matutino, Horario Vespertino"
                    />
                  </div>

                  <div className="col-span-2">
                    <Label htmlFor="description">Descripción</Label>
                    <textarea
                      id="description"
                      value={scheduleFormData.description}
                      onChange={(e) => setScheduleFormData({ ...scheduleFormData, description: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                      rows={2}
                      placeholder="Descripción del horario"
                    />
                  </div>

                  <div className="col-span-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isActive"
                        checked={scheduleFormData.isActive}
                        onChange={(e) => setScheduleFormData({ ...scheduleFormData, isActive: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <Label htmlFor="isActive" className="ml-2">
                        Horario activo
                      </Label>
                    </div>
                  </div>

                  <div className="col-span-2">
                    <Label>Horarios Disponibles</Label>
                    <p className="text-sm text-gray-500 mb-3">
                      Define los días y horarios disponibles de la institución
                    </p>
                    <AvailabilityScheduler
                      value={scheduleFormData.tiemposDisponibles}
                      onChange={(value) => setScheduleFormData({ ...scheduleFormData, tiemposDisponibles: value })}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsScheduleDialogOpen(false);
                      resetScheduleForm();
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit">
                    {editingSchedule ? 'Actualizar' : 'Crear'} Horario
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Días Festivos Tab */}
        <TabsContent value="holidays">
          <Card className="mb-4">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-50 rounded-lg">
                    <Calendar className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Saltar días festivos en la planificación</p>
                    <p className="text-sm text-gray-500">
                      Cuando está activo, la generación de horarios omite automáticamente los días festivos registrados.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500">{skipHolidays ? 'Activo' : 'Inactivo'}</span>
                  <Switch
                    checked={skipHolidays}
                    onCheckedChange={(val) => saveSkipHolidays(val)}
                    disabled={skipHolidaysSaving}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Días Festivos
                  </CardTitle>
                  <CardDescription>
                    Gestiona los días festivos y eventos especiales de la institución
                  </CardDescription>
                </div>
                <Button
                  onClick={() => {
                    resetHolidayForm();
                    setIsHolidayDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo Día Festivo
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {holidaysLoading ? (
                <p className="text-gray-500 text-center py-8">Cargando...</p>
              ) : holidays.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No hay días festivos registrados</p>
                </div>
              ) : (
                <div className="w-full">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Fecha
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Festividad
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Horario
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Estado
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {holidays.map((holiday) => (
                        <tr key={holiday.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {(() => {
                              const str = typeof holiday.fecha === 'string'
                                ? holiday.fecha.split('T')[0]
                                : (holiday.fecha as Date).toISOString().split('T')[0];
                              const [y, m, d] = str.split('-').map(Number);
                              return new Date(y, m - 1, d).toLocaleDateString('es-MX', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              });
                            })()}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {holiday.festividad}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {formatTiemposDisponibles(holiday.tiemposDisponibles)}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <span
                              className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                                holiday.isActive
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {holiday.isActive ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleEditHoliday(holiday)}
                              className="text-blue-600 hover:text-blue-900 mr-4"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteHoliday(holiday.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Dialog open={isHolidayDialogOpen} onOpenChange={setIsHolidayDialogOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>
                  {editingHoliday ? 'Editar Día Festivo' : 'Nuevo Día Festivo'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleHolidaySubmit} className="space-y-4">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="fecha">Fecha *</Label>
                    <Input
                      id="fecha"
                      type="date"
                      value={holidayFormData.fecha}
                      onChange={(e) => setHolidayFormData({ ...holidayFormData, fecha: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="festividad">Festividad *</Label>
                    <Input
                      id="festividad"
                      value={holidayFormData.festividad}
                      onChange={(e) => setHolidayFormData({ ...holidayFormData, festividad: e.target.value })}
                      required
                      placeholder="Ej: Día de la Independencia, Navidad"
                    />
                  </div>

                  <div>
                    <Label>Horario Especial (opcional)</Label>
                    <p className="text-xs text-gray-500 mb-2">
                      Define horarios especiales para este día festivo. Dejar vacío si es día completo festivo.
                    </p>
                    <HolidayTimeRanges
                      value={holidayFormData.tiemposDisponibles}
                      onChange={(value) => setHolidayFormData({ ...holidayFormData, tiemposDisponibles: value })}
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="holidayActive"
                      checked={holidayFormData.isActive}
                      onChange={(e) => setHolidayFormData({ ...holidayFormData, isActive: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <Label htmlFor="holidayActive" className="ml-2">
                      Día festivo activo
                    </Label>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsHolidayDialogOpen(false);
                      resetHolidayForm();
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit">
                    {editingHoliday ? 'Actualizar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Periodos Vacacionales Tab */}
        <TabsContent value="vacation-periods">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Umbrella className="w-5 h-5" />
                    Periodos Vacacionales
                  </CardTitle>
                  <CardDescription>
                    Define rangos de fechas de vacaciones. Las clases no se programarán durante estos periodos.
                  </CardDescription>
                </div>
                <Button
                  onClick={() => {
                    setEditingVacation(null);
                    setVacationFormData({ nombre: '', fechaInicio: '', fechaFin: '', isActive: true });
                    setIsVacationDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo Periodo
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {vacationPeriodsLoading ? (
                <p className="text-gray-500 text-center py-8">Cargando...</p>
              ) : vacationPeriodsList.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Umbrella className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 font-medium">No hay periodos vacacionales registrados</p>
                  <p className="text-gray-400 text-sm mt-1">Agrega periodos para bloquear días en la planificación de horarios</p>
                </div>
              ) : (
                <div className="w-full">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Inicio</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Fin</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duración</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {vacationPeriodsList.map((vp) => (
                        <tr key={vp.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Umbrella className="w-4 h-4 text-blue-400 flex-shrink-0" />
                              <span className="font-medium text-gray-900">{vp.nombre}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">{formatDateRange(vp.fechaInicio, vp.fechaInicio)}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{formatDateRange(vp.fechaFin, vp.fechaFin)}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {countVacationDays(vp.fechaInicio, vp.fechaFin)} día{countVacationDays(vp.fechaInicio, vp.fechaFin) !== 1 ? 's' : ''}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${vp.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                              {vp.isActive ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="ghost" size="sm" onClick={() => handleEditVacation(vp)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteVacation(vp.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Vacation Period Dialog */}
          <Dialog open={isVacationDialogOpen} onOpenChange={setIsVacationDialogOpen}>
            <DialogContent className="sm:max-w-[480px]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Umbrella className="w-5 h-5" />
                  {editingVacation ? 'Editar Periodo Vacacional' : 'Nuevo Periodo Vacacional'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleVacationSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="vp-nombre">Nombre del periodo *</Label>
                  <Input
                    id="vp-nombre"
                    placeholder="ej. Vacaciones de Semana Santa"
                    value={vacationFormData.nombre}
                    onChange={(e) => setVacationFormData({ ...vacationFormData, nombre: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="vp-inicio">Fecha de inicio *</Label>
                    <Input
                      id="vp-inicio"
                      type="date"
                      value={vacationFormData.fechaInicio}
                      onChange={(e) => setVacationFormData({ ...vacationFormData, fechaInicio: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vp-fin">Fecha de fin *</Label>
                    <Input
                      id="vp-fin"
                      type="date"
                      value={vacationFormData.fechaFin}
                      min={vacationFormData.fechaInicio}
                      onChange={(e) => setVacationFormData({ ...vacationFormData, fechaFin: e.target.value })}
                      required
                    />
                  </div>
                </div>
                {vacationFormData.fechaInicio && vacationFormData.fechaFin && vacationFormData.fechaInicio <= vacationFormData.fechaFin && (
                  <p className="text-sm text-blue-600 bg-blue-50 rounded-md px-3 py-2">
                    Se bloquearán <strong>{countVacationDays(vacationFormData.fechaInicio, vacationFormData.fechaFin)}</strong> día(s) en la planificación de horarios.
                  </p>
                )}
                <div className="flex items-center gap-3">
                  <Switch
                    id="vp-active"
                    checked={vacationFormData.isActive}
                    onCheckedChange={(val) => setVacationFormData({ ...vacationFormData, isActive: val })}
                  />
                  <Label htmlFor="vp-active">Periodo activo (bloquea la planificación)</Label>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setIsVacationDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={vacationSaving}>
                    {vacationSaving ? 'Guardando...' : editingVacation ? 'Actualizar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Finanzas Tab */}
        <TabsContent value="finanzas">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Configuración Financiera
              </CardTitle>
              <CardDescription>
                Configura las cuentas contables por defecto para el sistema de facturación y pagos
              </CardDescription>
            </CardHeader>
            <CardContent>
              {financeSettingsLoading ? (
                <p className="text-gray-500 text-center py-8">Cargando...</p>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="arAccount">Cuenta de Clientes (Cuentas por Cobrar)</Label>
                      <select
                        id="arAccount"
                        className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white"
                        value={financeSettings.arAccountId || ''}
                        onChange={(e) => setFinanceSettings(prev => ({ 
                          ...prev, 
                          arAccountId: e.target.value ? parseInt(e.target.value) : null 
                        }))}
                      >
                        <option value="">Selecciona una cuenta</option>
                        {accounts.filter(a => a.type === 'ASSET').map(acc => (
                          <option key={acc.id} value={acc.id}>
                            {acc.code} - {acc.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500">
                        Cuenta donde se registran las deudas de los clientes (tipo Activo)
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="salesAccount">Cuenta de Ingresos por Ventas</Label>
                      <select
                        id="salesAccount"
                        className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white"
                        value={financeSettings.salesAccountId || ''}
                        onChange={(e) => setFinanceSettings(prev => ({ 
                          ...prev, 
                          salesAccountId: e.target.value ? parseInt(e.target.value) : null 
                        }))}
                      >
                        <option value="">Selecciona una cuenta</option>
                        {accounts.filter(a => a.type === 'INCOME').map(acc => (
                          <option key={acc.id} value={acc.id}>
                            {acc.code} - {acc.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500">
                        Cuenta de ingresos por defecto (tipo Ingreso)
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="taxAccount">Cuenta de Impuestos por Pagar</Label>
                      <select
                        id="taxAccount"
                        className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white"
                        value={financeSettings.taxPayableAccountId || ''}
                        onChange={(e) => setFinanceSettings(prev => ({ 
                          ...prev, 
                          taxPayableAccountId: e.target.value ? parseInt(e.target.value) : null 
                        }))}
                      >
                        <option value="">Selecciona una cuenta</option>
                        {accounts.filter(a => a.type === 'LIABILITY').map(acc => (
                          <option key={acc.id} value={acc.id}>
                            {acc.code} - {acc.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500">
                        Cuenta para IVA y otros impuestos (tipo Pasivo)
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bankAccount">Cuenta de Banco Principal</Label>
                      <select
                        id="bankAccount"
                        className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white"
                        value={financeSettings.bankAccountId || ''}
                        onChange={(e) => setFinanceSettings(prev => ({ 
                          ...prev, 
                          bankAccountId: e.target.value ? parseInt(e.target.value) : null 
                        }))}
                      >
                        <option value="">Selecciona una cuenta</option>
                        {accounts.filter(a => a.type === 'ASSET').map(acc => (
                          <option key={acc.id} value={acc.id}>
                            {acc.code} - {acc.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500">
                        Cuenta de banco para registrar pagos recibidos (tipo Activo)
                      </p>
                    </div>
                  </div>

                  {/* Discount & Surcharge Accounts for Tuition */}
                  <div className="pt-4 border-t">
                    <div className="mb-3">
                      <h4 className="font-medium text-gray-900 text-sm">Cuentas de Ajuste — Colegiaturas</h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Cuentas utilizadas para los asientos de descuento por pronto pago y recargo por mora en el registro de pagos.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="discountAccount">Cuenta de Descuento (Pronto Pago)</Label>
                        <select
                          id="discountAccount"
                          className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white"
                          value={financeSettings.discountAccountId || ''}
                          onChange={(e) => setFinanceSettings(prev => ({
                            ...prev,
                            discountAccountId: e.target.value ? parseInt(e.target.value) : null
                          }))}
                        >
                          <option value="">Sin configurar (usa cuenta de ingresos)</option>
                          {accounts.map(acc => (
                            <option key={acc.id} value={acc.id}>
                              {acc.code} - {acc.name} ({acc.type})
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500">
                          Cargo de descuento al aplicar pronto pago. Recomendado: cuenta tipo Gasto o Contra-ingreso.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="surchargeAccount">Cuenta de Recargo (Mora)</Label>
                        <select
                          id="surchargeAccount"
                          className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white"
                          value={financeSettings.surchargeAccountId || ''}
                          onChange={(e) => setFinanceSettings(prev => ({
                            ...prev,
                            surchargeAccountId: e.target.value ? parseInt(e.target.value) : null
                          }))}
                        >
                          <option value="">Sin configurar (usa cuenta de ingresos)</option>
                          {accounts.map(acc => (
                            <option key={acc.id} value={acc.id}>
                              {acc.code} - {acc.name} ({acc.type})
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500">
                          Ingreso adicional por recargo en pago tardío. Recomendado: cuenta tipo Ingreso.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="creditAccount">Cuenta de Anticipos / Saldos a Favor</Label>
                        <select
                          id="creditAccount"
                          className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white"
                          value={financeSettings.creditAccountId || ''}
                          onChange={(e) => setFinanceSettings(prev => ({
                            ...prev,
                            creditAccountId: e.target.value ? parseInt(e.target.value) : null
                          }))}
                        >
                          <option value="">Sin configurar</option>
                          {accounts.map(acc => (
                            <option key={acc.id} value={acc.id}>
                              {acc.code} - {acc.name} ({acc.type})
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500">
                          Pasivo utilizado para registrar saldos a favor de alumnos (anticipos). Recomendado: cuenta tipo Pasivo — "Anticipos de Clientes".
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Auto Charge Generation Config */}
                  <div className="pt-4 border-t">
                    <div className="mb-3">
                      <h4 className="font-medium text-gray-900 text-sm">Generaciones Automáticas de Cargos</h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Configura qué producto se usará para generar cuentas de cobro automáticamente en cada evento del sistema.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="inscriptionProduct">Inscripción</Label>
                        <select
                          id="inscriptionProduct"
                          className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white"
                          value={financeSettings.inscriptionProductId || ''}
                          onChange={(e) => setFinanceSettings(prev => ({
                            ...prev,
                            inscriptionProductId: e.target.value ? parseInt(e.target.value) : null
                          }))}
                        >
                          <option value="">Sin configurar</option>
                          {productsList.filter(p => p).map(product => (
                            <option key={product.id} value={product.id}>
                              {product.sku} — {product.name} (${parseFloat(product.price).toFixed(2)})
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500">
                          Producto que se facturará automáticamente al validar la inscripción de un alumno aspirante.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Tuition Eligible Statuses */}
                  <div className="pt-4 border-t space-y-3">
                    <div>
                      <h4 className="font-medium text-gray-900 text-sm">Estatus de Alumno para Generación de Colegiaturas</h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Solo se generarán cuotas y recibos para alumnos que tengan uno de estos estatus.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {financeSettings.tuitionEligibleStatuses.map(status => (
                        <span
                          key={status}
                          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                        >
                          {status}
                          <button
                            type="button"
                            onClick={() => setFinanceSettings(prev => ({
                              ...prev,
                              tuitionEligibleStatuses: prev.tuitionEligibleStatuses.filter(s => s !== status),
                            }))}
                            className="text-blue-600 hover:text-blue-900 leading-none"
                            title="Quitar estatus"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      {financeSettings.tuitionEligibleStatuses.length === 0 && (
                        <p className="text-xs text-red-500">Debes tener al menos un estatus configurado.</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Nuevo estatus (ej: becado)"
                        value={newStatusInput}
                        onChange={e => setNewStatusInput(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const val = newStatusInput.trim().toLowerCase();
                            if (val && !financeSettings.tuitionEligibleStatuses.includes(val)) {
                              setFinanceSettings(prev => ({
                                ...prev,
                                tuitionEligibleStatuses: [...prev.tuitionEligibleStatuses, val],
                              }));
                            }
                            setNewStatusInput('');
                          }
                        }}
                        className="flex-1 max-w-xs px-3 py-1.5 border rounded-md text-sm text-gray-900 bg-white"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const val = newStatusInput.trim().toLowerCase();
                          if (val && !financeSettings.tuitionEligibleStatuses.includes(val)) {
                            setFinanceSettings(prev => ({
                              ...prev,
                              tuitionEligibleStatuses: [...prev.tuitionEligibleStatuses, val],
                            }));
                          }
                          setNewStatusInput('');
                        }}
                      >
                        Agregar
                      </Button>
                    </div>
                    <p className="text-xs text-gray-400">
                      Estatus comunes: <span className="font-medium">activo</span>, <span className="font-medium">aceptado</span>, <span className="font-medium">aspirante</span>, <span className="font-medium">nuevo</span>. Presiona Enter o el botón para agregar.
                    </p>
                  </div>

                  <div className="pt-4 border-t">
                    <Button onClick={saveFinanceSettings} disabled={financeSaving || financeSettings.tuitionEligibleStatuses.length === 0}>
                      {financeSaving ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Guardando...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Guardar Configuración
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Facturación Tab */}
        <TabsContent value="facturacion">
          <BillingSettings />
        </TabsContent>

        {/* API Keys Tab */}
        <TabsContent value="apikeys">

      {/* API Keys Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            API Keys
          </CardTitle>
          <CardDescription>
            Crea y gestiona las claves API para sistemas externos. Las claves solo se muestran una vez al crearlas.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Create API Key Form */}
          {showCreateApiKeyForm ? (
            <div className="border rounded-lg p-4 mb-4 bg-gray-50">
              <h3 className="font-medium mb-3">Crear Nueva API Key</h3>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1">Nombre</label>
                  <Input
                    placeholder="ej: Integración CRM"
                    value={newApiKeyName}
                    onChange={(e) => setNewApiKeyName(e.target.value)}
                  />
                </div>
                <Button
                  onClick={handleCreateApiKey}
                  disabled={apiKeyLoading || !newApiKeyName.trim()}
                >
                  {apiKeyLoading ? 'Creando...' : 'Crear'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowCreateApiKeyForm(false);
                    setNewApiKeyName('');
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <div className="mb-4">
              <Button onClick={() => setShowCreateApiKeyForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Nueva API Key
              </Button>
            </div>
          )}

          {/* Created API Key Display */}
          {createdApiKey && (
            <div className="border border-green-200 rounded-lg p-4 mb-4 bg-green-50">
              <h3 className="font-medium text-green-800 mb-2">¡API Key Creada Exitosamente!</h3>
              <p className="text-sm text-green-700 mb-3">
                Guarda esta clave ahora. Por seguridad, no se mostrará nuevamente.
              </p>
              <div className="flex items-center gap-2">
                <Input
                  value={createdApiKey}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => copyToClipboard(createdApiKey)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <Button 
                size="sm" 
                variant="outline" 
                className="mt-3"
                onClick={() => setCreatedApiKey(null)}
              >
                Cerrar
              </Button>
            </div>
          )}

          {/* API Keys List */}
          <div className="space-y-3">
            {apiKeys.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Key className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No hay API Keys configuradas</p>
                <p className="text-sm">Crea tu primera API Key para comenzar</p>
              </div>
            ) : (
              apiKeys.map((apiKey) => (
                <div key={apiKey.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">{apiKey.name}</h4>
                      <p className="text-sm text-gray-500">
                        Creada: {new Date(apiKey.createdAt).toLocaleDateString()}
                        {apiKey.lastUsedAt && (
                          <span className="ml-2">
                            | Último uso: {new Date(apiKey.lastUsedAt).toLocaleDateString()}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        apiKey.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {apiKey.isActive ? 'Activa' : 'Inactiva'}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => regenerateApiKey(apiKey.id, apiKey.name)}
                        disabled={apiKeyLoading}
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteApiKey(apiKey.id, apiKey.name)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {/* CRM Webhook Tab */}
        <TabsContent value="webhooks">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Webhook className="w-5 h-5" />
            Integración CRM (Webhook)
          </CardTitle>
          <CardDescription>
            Configura la URL de callback para sincronizar automáticamente los datos con tu sistema CRM.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="webhook-enabled"
                checked={webhookConfig.enabled}
                onChange={(e) => setWebhookConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                className="w-4 h-4"
              />
              <label htmlFor="webhook-enabled" className="font-medium">
                Habilitar webhook CRM
              </label>
            </div>

            {/* Webhook URL */}
            <div>
              <label className="block text-sm font-medium mb-1">URL del Webhook</label>
              <Input
                placeholder="https://tu-crm.com/webhook/qcampusone"
                value={webhookConfig.url}
                onChange={(e) => setWebhookConfig(prev => ({ ...prev, url: e.target.value }))}
                disabled={!webhookConfig.enabled}
              />
              <p className="text-xs text-gray-500 mt-1">
                Esta URL recibirá las notificaciones cuando se creen o actualicen estudiantes
              </p>
            </div>

            {/* Secret Key */}
            <div>
              <label className="block text-sm font-medium mb-1">Clave Secreta (Opcional)</label>
              <Input
                type="password"
                placeholder="Clave para verificar la autenticidad"
                value={webhookConfig.secretKey}
                onChange={(e) => setWebhookConfig(prev => ({ ...prev, secretKey: e.target.value }))}
                disabled={!webhookConfig.enabled}
              />
              <p className="text-xs text-gray-500 mt-1">
                Se enviará en el header X-Signature para verificar que la llamada proviene de QCampusOne
              </p>
            </div>

            {/* Events */}
            <div>
              <label className="block text-sm font-medium mb-2">Eventos a Notificar</label>
              <div className="space-y-2">
                {[
                  { id: 'student_created', label: 'Estudiante creado' },
                  { id: 'student_updated', label: 'Estudiante actualizado' },
                  { id: 'api_created', label: 'Creación vía API externa' },
                  { id: 'api_updated', label: 'Actualización vía API externa' }
                ].map((event) => (
                  <div key={event.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={event.id}
                      checked={webhookConfig.events.includes(event.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setWebhookConfig(prev => ({
                            ...prev,
                            events: [...prev.events, event.id]
                          }));
                        } else {
                          setWebhookConfig(prev => ({
                            ...prev,
                            events: prev.events.filter(ev => ev !== event.id)
                          }));
                        }
                      }}
                      disabled={!webhookConfig.enabled}
                    />
                    <label htmlFor={event.id} className="text-sm">
                      {event.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Test Result */}
            {webhookConfig.lastTestResult && (
              <div className={`border rounded-lg p-3 ${
                webhookConfig.lastTestResult.success 
                  ? 'border-green-200 bg-green-50' 
                  : 'border-red-200 bg-red-50'
              }`}>
                <div className="flex items-center gap-2">
                  {webhookConfig.lastTestResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className={`font-medium ${
                    webhookConfig.lastTestResult.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {webhookConfig.lastTestResult.success ? 'Prueba Exitosa' : 'Error en la Prueba'}
                  </span>
                </div>
                <p className="text-sm mt-1">
                  {new Date(webhookConfig.lastTestResult.timestamp).toLocaleString()}
                </p>
                {webhookConfig.lastTestResult.error && (
                  <p className="text-sm text-red-700 mt-1">
                    {webhookConfig.lastTestResult.error}
                  </p>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={saveWebhookConfig}
                disabled={webhookLoading || !webhookConfig.enabled}
              >
                <Save className="w-4 h-4 mr-2" />
                {webhookLoading ? 'Guardando...' : 'Guardar Configuración'}
              </Button>
              
              <Button
                variant="outline"
                onClick={testWebhook}
                disabled={testingWebhook || !webhookConfig.enabled || !webhookConfig.url}
              >
                <TestTube className="w-4 h-4 mr-2" />
                {testingWebhook ? 'Probando...' : 'Probar Webhook'}
              </Button>
            </div>

            {/* Usage Instructions */}
            <div className="border-t pt-4 mt-6">
              <h4 className="font-medium mb-2">Instrucciones de Uso</h4>
              <div className="text-sm text-gray-600 space-y-2">
                <p><strong>1. API Externa:</strong> Usa el endpoint <code className="bg-gray-100 px-1 rounded">/api/v1/alumnos</code> con el header <code className="bg-gray-100 px-1 rounded">X-API-Key</code></p>
                <p><strong>2. Webhook:</strong> Tu CRM recibirá un POST con los datos del estudiante en el cuerpo</p>
                <p><strong>3. Seguridad:</strong> Si configuras una clave secreta, verifica el header <code className="bg-gray-100 px-1 rounded">X-Signature</code></p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {/* Email Configuration Tab */}
        <TabsContent value="email">
          <EmailConfigurationSettings />
        </TabsContent>

        {/* AI Bubble Tab */}
        <TabsContent value="aibubble">
          {scriptError && (
            <Card className="border-red-200 bg-red-50 mb-4">
              <CardContent className="pt-6">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <p className="text-red-800">{scriptError}</p>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Code className="h-5 w-5 text-blue-600" />
                <CardTitle>Script de la Burbuja de AI</CardTitle>
              </div>
              <CardDescription>
                Pega el código HTML/JavaScript de tu widget de chat. Este script se ejecutará automáticamente en todas las páginas de tu tenant.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Código HTML/JavaScript
                </label>
                <textarea 
                  value={customScript} 
                  onChange={(e) => setCustomScript(e.target.value)}
                  placeholder="Pega aquí tu código <script>...</script>"
                  className="w-full min-h-[300px] p-4 font-mono text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  ⚠️ <strong>Importante:</strong> Solo usa scripts de fuentes confiables. Este código se ejecutará en todas las páginas.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-900 mb-2">💡 Ejemplo de widget de chat con datos del usuario:</p>
                <code className="text-xs text-blue-700 block whitespace-pre-wrap">
{`<script>
(function() {
    // ========================================
    // CONFIGURACIÓN: Datos del Usuario
    // ========================================
    // ✨ AUTO-POBLADO: El sistema inyecta automáticamente los datos del usuario
    // Puedes acceder a ellos desde window.__USER_DATA__
    const USER_DATA = window.__USER_DATA__ || {
        userId: null,        // ID único del usuario
        userName: null,      // Nombre completo del usuario
        userEmail: null,     // Email del usuario
        userRolName: null,   // Nombre del rol del usuario
        tenantId: null,      // ID del tenant/empresa
    };
    
    // ========================================
    // CONFIGURACIÓN: URL del Chat
    // ========================================
    // Personaliza la URL con los datos del usuario
    const IFRAME_URL = 'https://tu-chat-ai.com/embed/123?user=' + 
                       encodeURIComponent(USER_DATA.userName || 'Anónimo') + 
                       '&email=' + encodeURIComponent(USER_DATA.userEmail || '');
    
    // ========================================
    // Estilos del botón flotante
    // ========================================
    const style = document.createElement('style');
    style.innerHTML = \\\`
        #ai-chat-button {
            position: fixed;
            bottom: 20px;
            right: 20px;
            width: 60px;
            height: 60px;
            background-color: #2563eb;
            border-radius: 50%;
            cursor: pointer;
            box-shadow: 0 4px 12px rgba(0,0,0,0.2);
            z-index: 9998;
        }
        #ai-chat-iframe {
            position: fixed;
            bottom: 100px;
            right: 20px;
            width: 350px;
            height: 500px;
            border: none;
            border-radius: 10px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.2);
            display: none;
            z-index: 9999;
        }
    \\\`;
    document.head.appendChild(style);
    
    // ========================================
    // Crear elementos
    // ========================================
    const iframe = document.createElement('iframe');
    iframe.id = 'ai-chat-iframe';
    iframe.src = IFRAME_URL;
    document.body.appendChild(iframe);
    
    const button = document.createElement('div');
    button.id = 'ai-chat-button';
    button.innerHTML = '💬';
    button.title = 'Chat con ' + (USER_DATA.userName || 'Soporte');
    document.body.appendChild(button);
    
    // Toggle chat
    button.addEventListener('click', () => {
        iframe.style.display = 
            iframe.style.display === 'none' ? 'block' : 'none';
    });
})();
</script>`}
                </code>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-sm font-medium text-emerald-900 mb-2">✨ Datos del Usuario Auto-poblados:</p>
                <p className="text-sm text-emerald-800 mb-2">
                  El sistema inyecta automáticamente <code className="bg-emerald-100 px-1 rounded">window.__USER_DATA__</code> con información del usuario autenticado:
                </p>
                <ul className="text-sm text-emerald-800 space-y-1 list-disc list-inside">
                  <li><strong>userId:</strong> ID único del usuario</li>
                  <li><strong>userName:</strong> Nombre completo del usuario</li>
                  <li><strong>userEmail:</strong> Email del usuario</li>
                  <li><strong>userRolName:</strong> Nombre del rol del usuario</li>
                  <li><strong>tenantId:</strong> ID del tenant/empresa</li>
                </ul>
                <p className="text-xs text-emerald-700 mt-2">
                  💡 Úsalo para personalizar conversaciones, pre-llenar formularios, o identificar usuarios en tu chat.
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm font-medium text-amber-900 mb-2">📋 Características:</p>
                <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
                  <li>El script se ejecuta automáticamente en todas las páginas</li>
                  <li>Solo los usuarios de tu tenant verán este widget</li>
                  <li>Se limpia automáticamente al cerrar sesión</li>
                  <li>Soporta scripts anidados y HTML complejo</li>
                  <li>Los datos del usuario se actualizan en cada sesión</li>
                </ul>
              </div>

              <Button 
                onClick={saveAIBubbleScript}
                disabled={scriptLoading}
                className={`w-full ${scriptSuccess ? 'bg-green-600 hover:bg-green-700' : ''}`}
              >
                {scriptLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Guardando...
                  </>
                ) : scriptSuccess ? (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    ¡Script Guardado Exitosamente!
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Guardar Script de la Burbuja
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>¿Cómo funciona?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-gray-700">
              <div>
                <strong className="text-gray-900">1. Obtén tu código:</strong>
                <p className="mt-1">Copia el código de integración de tu proveedor de chat (Intercom, Zendesk, custom, etc.)</p>
              </div>
              <div>
                <strong className="text-gray-900">2. Pégalo aquí:</strong>
                <p className="mt-1">Simplemente pega el código completo en el campo de texto de arriba</p>
              </div>
              <div>
                <strong className="text-gray-900">3. Guarda y listo:</strong>
                <p className="mt-1">El widget aparecerá automáticamente en todas las páginas de tu plataforma</p>
              </div>
              <div>
                <strong className="text-gray-900">4. Aislamiento total:</strong>
                <p className="mt-1">Solo los usuarios de tu tenant verán este widget, otros tenants no lo verán</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Agente Local Tab */}
        <TabsContent value="agente-local">
          <div className="space-y-6">
            {agentLoading ? (
              <Card>
                <CardContent className="py-12 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Header card */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                          <Bot className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <CardTitle>Agente Local de AI</CardTitle>
                          <CardDescription>
                            Configura el asistente virtual con Gemini AI. Cuando está activo, reemplaza la burbuja de chat con un agente inteligente personalizado.
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600">
                          {agentConfig.isActive ? 'Activo' : 'Inactivo'}
                        </span>
                        <Switch
                          checked={agentConfig.isActive}
                          onCheckedChange={(v) => setAgentConfig(prev => ({ ...prev, isActive: v }))}
                        />
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                {/* Identity */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Identidad del Agente</CardTitle>
                    <CardDescription>Define quién es el agente y qué debe lograr.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="agent-name">Nombre del Agente</Label>
                      <Input
                        id="agent-name"
                        value={agentConfig.name}
                        onChange={(e) => setAgentConfig(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="ej. Asistente Escolar, Ana, Max..."
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="agent-personality">Personalidad</Label>
                      <textarea
                        id="agent-personality"
                        value={agentConfig.personality ?? ''}
                        onChange={(e) => setAgentConfig(prev => ({ ...prev, personality: e.target.value }))}
                        placeholder="ej. Amable, profesional, paciente. Usa un tono cercano pero formal."
                        rows={3}
                        className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                      />
                    </div>
                    <div>
                      <Label htmlFor="agent-objective">Objetivo Principal</Label>
                      <textarea
                        id="agent-objective"
                        value={agentConfig.objective ?? ''}
                        onChange={(e) => setAgentConfig(prev => ({ ...prev, objective: e.target.value }))}
                        placeholder="ej. Asistir a alumnos y padres con información sobre procesos de inscripción, pagos y horarios."
                        rows={3}
                        className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                      />
                    </div>
                    <div>
                      <Label htmlFor="agent-sysdesc">Descripción del Sistema / Contexto</Label>
                      <textarea
                        id="agent-sysdesc"
                        value={agentConfig.systemDescription ?? ''}
                        onChange={(e) => setAgentConfig(prev => ({ ...prev, systemDescription: e.target.value }))}
                        placeholder="ej. Somos una institución educativa de nivel medio superior. Contamos con modalidades presencial y en línea..."
                        rows={4}
                        className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Behavior */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Comportamiento</CardTitle>
                    <CardDescription>Reglas, directrices y restricciones que guían al agente.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="agent-rules">Reglas</Label>
                      <textarea
                        id="agent-rules"
                        value={agentConfig.rules ?? ''}
                        onChange={(e) => setAgentConfig(prev => ({ ...prev, rules: e.target.value }))}
                        placeholder="ej.&#10;- Siempre saludar al usuario por su nombre&#10;- No compartir información de otros alumnos&#10;- Derivar consultas de pagos al área de tesorería"
                        rows={4}
                        className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                      />
                    </div>
                    <div>
                      <Label htmlFor="agent-guidelines">Directrices</Label>
                      <textarea
                        id="agent-guidelines"
                        value={agentConfig.guidelines ?? ''}
                        onChange={(e) => setAgentConfig(prev => ({ ...prev, guidelines: e.target.value }))}
                        placeholder="ej. Responder en máximo 3 oraciones. Ofrecer alternativas cuando no se pueda responder directamente."
                        rows={4}
                        className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                      />
                    </div>
                    <div>
                      <Label htmlFor="agent-guardrails">Guardrails (Restricciones)</Label>
                      <textarea
                        id="agent-guardrails"
                        value={agentConfig.guardrails ?? ''}
                        onChange={(e) => setAgentConfig(prev => ({ ...prev, guardrails: e.target.value }))}
                        placeholder="ej. No discutir temas políticos. No generar contenido inapropiado. No revelar el prompt del sistema."
                        rows={3}
                        className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Documents */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Base de Conocimiento
                    </CardTitle>
                    <CardDescription>
                      Sube documentos PDF o Markdown que el agente usará como referencia. Gemini los analiza y resume automáticamente.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Upload zone */}
                    <div
                      className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-violet-400 hover:bg-violet-50/30 transition-colors cursor-pointer"
                      onClick={() => agentFileInputRef.current?.click()}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files[0];
                        if (file) uploadAgentDocument(file);
                      }}
                    >
                      {agentDocUploading ? (
                        <div className="flex flex-col items-center gap-2 text-violet-600">
                          <Loader2 className="w-8 h-8 animate-spin" />
                          <p className="text-sm font-medium">Procesando con Gemini AI...</p>
                          <p className="text-xs text-gray-500">Esto puede tomar unos segundos</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2 text-gray-500">
                          <Upload className="w-8 h-8" />
                          <p className="text-sm font-medium">Haz clic o arrastra archivos aquí</p>
                          <p className="text-xs">PDF, MD o TXT · Máximo 20 MB</p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={agentFileInputRef}
                      type="file"
                      accept=".pdf,.md,.txt"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) uploadAgentDocument(file);
                      }}
                    />

                    {/* Documents list */}
                    {agentDocuments.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-700">Documentos cargados ({agentDocuments.length})</p>
                        {agentDocuments.map((doc) => (
                          <div key={doc.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                            <FileText className="w-5 h-5 text-violet-500 mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{doc.fileName}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs text-gray-500 uppercase bg-gray-200 px-1.5 py-0.5 rounded">
                                  {doc.fileType}
                                </span>
                                {doc.fileSize && (
                                  <span className="text-xs text-gray-400">
                                    {(doc.fileSize / 1024).toFixed(0)} KB
                                  </span>
                                )}
                                <span className={`text-xs px-1.5 py-0.5 rounded ${doc.isProcessed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                  {doc.isProcessed ? 'Procesado' : 'Pendiente'}
                                </span>
                              </div>
                              {doc.summary && (
                                <p className="text-xs text-gray-500 mt-1.5 line-clamp-2">{doc.summary}</p>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteAgentDocument(doc.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Save button */}
                <div className="flex justify-end">
                  <Button
                    onClick={saveAgentConfig}
                    disabled={agentSaving}
                    className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
                  >
                    {agentSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Guardar Configuración
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="estatus-alumnos">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart2 className="w-5 h-5" />
                Evaluación de Estatus de Alumnos
              </CardTitle>
              <CardDescription>
                Evalúa automáticamente el estatus de todos los alumnos elegibles según las reglas del sistema:
                adeudos vencidos (activo → deudor → baja administrativa), programas concluidos y egresados.
                Se ejecuta automáticamente todos los días a las 2:00 AM. También puedes ejecutarlo manualmente.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">

              {/* Filtro de asistencia/calificaciones por estatus de adeudo */}
              <div className="flex items-center justify-between rounded-lg border p-4 bg-gray-50">
                <div>
                  <p className="font-medium text-gray-900">Bloquear asistencia y calificaciones por adeudo</p>
                  <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                    Cuando está activo, los profesores no pueden registrar asistencia ni calificaciones de alumnos
                    en estatus <strong>deudor</strong>, <strong>aspirante</strong> o <strong>baja administrativa</strong>.
                    Desactívalo temporalmente si hay un atraso administrativo en el registro de pagos para permitir
                    que se sigan tomando asistencias y calificaciones mientras se regulariza. Los alumnos dados de
                    baja definitiva nunca se muestran, sin importar esta opción.
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                  <span className="text-sm text-gray-500">{debtStatusFilterEnabled ? 'Activo' : 'Inactivo'}</span>
                  <Switch
                    checked={debtStatusFilterEnabled}
                    onCheckedChange={(val) => saveDebtStatusFilter(val)}
                    disabled={debtStatusFilterSaving}
                  />
                </div>
              </div>

              {/* Reglas activas */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Reglas activas</h3>
                  <Button variant="outline" size="sm" onClick={loadRulesManifest} className="flex items-center gap-2 text-xs">
                    <FileText className="w-3.5 h-3.5" />
                    Ver reglas cargadas
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="border rounded-lg p-3 bg-orange-50 border-orange-200">
                    <p className="text-sm font-semibold text-orange-800">Sincronización de Adeudos</p>
                    <p className="text-xs text-orange-700 mt-1">
                      0 cuotas vencidas → <strong>activo</strong><br />
                      1+ cuotas vencidas → <strong>deudor</strong><br />
                      2+ en un mismo plan → <strong>baja administrativa</strong>
                    </p>
                  </div>
                  <div className="border rounded-lg p-3 bg-blue-50 border-blue-200">
                    <p className="text-sm font-semibold text-blue-800">Programa Concluido</p>
                    <p className="text-xs text-blue-700 mt-1">
                      Si la fecha de fin del período ya pasó y el alumno tiene inscripción activa → <strong>programa concluido</strong>
                    </p>
                  </div>
                  <div className="border rounded-lg p-3 bg-purple-50 border-purple-200">
                    <p className="text-sm font-semibold text-purple-800">Reincorporado → Activo</p>
                    <p className="text-xs text-purple-700 mt-1">
                      Alumno que cambió de grupo queda en <strong>reincorporado</strong> hasta que se registre su primera asistencia en el nuevo grupo → <strong>activo</strong>
                    </p>
                  </div>
                  <div className="border rounded-lg p-3 bg-green-50 border-green-200">
                    <p className="text-sm font-semibold text-green-800">Egresado</p>
                    <p className="text-xs text-green-700 mt-1">
                      Alumno en programa concluido con 100% de asistencia → <strong>egresado</strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* Resultado anterior */}
              {batchResult && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                  <p className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> Última ejecución completada
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-800">{batchResult.total}</p>
                      <p className="text-xs text-gray-500">Evaluados</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-700">{batchResult.applied}</p>
                      <p className="text-xs text-gray-500">Con cambio</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-500">{batchResult.skipped}</p>
                      <p className="text-xs text-gray-500">Sin cambio</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-600">{batchResult.errors}</p>
                      <p className="text-xs text-gray-500">Errores</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Duración: {(batchResult.durationMs / 1000).toFixed(1)}s</p>
                </div>
              )}

              {batchError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700">{batchError}</p>
                </div>
              )}

              {/* Botón ejecutar */}
              <Button onClick={runBatchEval} disabled={batchRunning} className="flex items-center gap-2">
                {batchRunning ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Evaluando alumnos...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Ejecutar evaluación ahora
                  </>
                )}
              </Button>
              <p className="text-xs text-gray-400">
                La ejecución automática ocurre diariamente a las 2:00 AM (hora del servidor). Puedes ejecutarla manualmente en cualquier momento.
              </p>

            </CardContent>
          </Card>

          {/* Modal: reglas cargadas en el engine */}
          <Dialog open={showRulesModal} onOpenChange={setShowRulesModal}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Reglas cargadas en el Engine de Estatus
                </DialogTitle>
              </DialogHeader>

              {rulesLoading ? (
                <div className="flex items-center justify-center py-8 gap-2 text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Cargando reglas...
                </div>
              ) : rulesManifest ? (
                <div className="space-y-5 mt-2">
                  {/* Trigger map */}
                  <div>
                    <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">Triggers → Reglas</h4>
                    <div className="rounded-lg border divide-y text-sm">
                      {Object.entries(rulesManifest.triggerMap).map(([trigger, ruleIds]) => (
                        <div key={trigger} className="flex items-start gap-3 px-3 py-2">
                          <span className="font-mono text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded whitespace-nowrap mt-0.5">{trigger}</span>
                          <span className="text-gray-600">{(ruleIds as string[]).join(', ') || '—'}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Rule details */}
                  <div>
                    <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">Detalle de reglas ({rulesManifest.rules.length})</h4>
                    <div className="space-y-3">
                      {rulesManifest.rules.map((rule) => (
                        <div key={rule.id} className="rounded-lg border p-3 space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-xs font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">{rule.id}</span>
                            <div className="flex gap-1 flex-wrap">
                              {rule.triggers.map((t) => (
                                <span key={t} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{t}</span>
                              ))}
                            </div>
                          </div>
                          <p className="text-xs text-gray-600">{rule.description}</p>
                          <div>
                            <p className="text-[10px] font-semibold uppercase text-gray-400 mb-1">Estatus elegibles</p>
                            <div className="flex gap-1 flex-wrap">
                              {rule.eligibleStatuses.map((s) => (
                                <span key={s} className="text-[10px] bg-yellow-50 text-yellow-700 border border-yellow-200 px-1.5 py-0.5 rounded">{s}</span>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold uppercase text-gray-400 mb-1">Transiciones</p>
                            <div className="space-y-1">
                              {rule.transitions.map((tr, i) => (
                                <div key={i} className="flex items-start gap-2 text-xs">
                                  <span className="text-gray-400 whitespace-nowrap">
                                    {Array.isArray(tr.from) ? tr.from.join(' / ') : tr.from}
                                  </span>
                                  <span className="text-gray-400">→</span>
                                  <span className="font-semibold text-gray-800 whitespace-nowrap">{tr.to}</span>
                                  <span className="text-gray-500">· {tr.condition}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-6">No se pudieron cargar las reglas.</p>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>

    </div>
  );
}